"use client";

import { useSession, useSupabaseClient } from "@/components/SupabaseProvider";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { upcomingAutoPostQueryKey } from "./useUpcomingAutoPostCount";
import toast from "react-hot-toast";
import type { AccountCadence } from "../components/publicationFlow/engine/types";
import { AUTO_POST } from "../components/autoPublish/autoPostName";
import { parseScheduleDefaults } from "../components/publicationFlow/scheduleConfig";
import { scheduleDefaultsQueryKey } from "./useScheduleDefaults";
import type { FolderModel } from "../models/folder_model";
import { applyAutoPublishProjectChange, autoPublishProjectsQueryKey, sortAutoPublishProjects } from "./useAutoPublishProjects";
import {
  isAuthenticatedSocialProviderAccount,
  useSocialProviderAccounts,
  type SocialProviderAccount,
} from "./useSocialProviderAccounts";

// A project's Auto publish (klap-api src/services/auto_publish_service.ts):
// folders.auto_publish owns each project's accounts, hours, start and timezone.
// profiles.auto_publish remembers defaults for new projects; account cadence
// remains shared across projects, which take turns on the same account. The server books, moves and removes the posts; this
// reads the settings and the project's upcoming Auto publish posts, and saves.

export type AutoPublishState = {
  enabled: boolean;
  /** Stored with this project; each settings write adopts the current browser zone. */
  timeZone?: string | null;
  accountIds: string[];
  cadences: Record<string, AccountCadence>;
  preferredHours: number[] | null;
  /** No post before it; null: as soon as possible. */
  startAt: Date | null;
  /** The project's Auto publish posts still to go out. */
  upcoming: number;
  nextPostAt: Date | null;
};
export type AutoPublishChange = {
  enabled?: boolean;
  accountIds?: string[];
  cadences?: Record<string, AccountCadence>;
  preferredHours?: number[];
  /** null: back to as soon as possible. */
  startAt?: Date | null;
};

export const autoPublishDefaultsQueryKey = (userId: string | undefined) => ["auto-publish-default", userId] as const;

export const autoPublishQueryKey = (folderId: string) => ["auto-publish", folderId] as const;

/** A change as the switch and chips show it at once, before the server's bookings follow. */
export const applyAutoPublishChange = (state: AutoPublishState, change: AutoPublishChange): AutoPublishState => ({
  ...state,
  ...(change.enabled !== undefined && { enabled: change.enabled }),
  ...(change.accountIds && { accountIds: change.accountIds }),
  ...(change.cadences && { cadences: { ...state.cadences, ...change.cadences } }),
  ...(change.preferredHours && { preferredHours: change.preferredHours }),
  ...(change.startAt !== undefined && { startAt: change.startAt }),
  ...(change.enabled === false && { upcoming: 0, nextPostAt: null }),
});

const ids = (value: unknown): string[] => (Array.isArray(value) ? value.filter((id): id is string => typeof id === "string") : []);
const record = (value: unknown) => (value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, any>) : {});

/** Where Auto publish can post: connected, not needing a reconnect, never LinkedIn. */
export const autoPublishAccounts = (accounts: readonly SocialProviderAccount[]) =>
  accounts.filter((account) => account.provider !== "linkedin" && isAuthenticatedSocialProviderAccount(account));

/** `refreshKey` reads again when it changes (the task turning ready: the server books then). */
export function useAutoPublish(folderId: string, { enabled = true, refreshKey }: { enabled?: boolean; refreshKey?: string } = {}) {
  const supabase = useSupabaseClient();
  const session = useSession();
  const userId = session?.user.id;
  const queryClient = useQueryClient();
  const queryKey = autoPublishQueryKey(folderId);

  const query = useQuery({
    queryKey: [...queryKey, refreshKey],
    enabled: enabled && Boolean(userId) && Boolean(folderId),
    // Null on someone else's project (admins can read any folder).
    queryFn: async (): Promise<AutoPublishState | null> => {
      const [folder, profile, upcoming] = await Promise.all([
        supabase.from("folders").select("author_id, auto_publish").eq("id", folderId).single(),
        supabase.from("profiles").select("auto_publish, schedule_defaults").eq("id", userId!).single(),
        supabase.from("publications").select("scheduled_at", { count: "exact" })
          .eq("auto_folder_id", folderId).eq("status", "scheduled").order("scheduled_at").limit(1),
      ]);
      if (folder.error) throw folder.error;
      if (profile.error) throw profile.error;
      if (upcoming.error) throw upcoming.error;
      if (folder.data.author_id !== userId) return null;
      const config = record(folder.data.auto_publish);
      const settings = record(profile.data.auto_publish);
      const defaults = parseScheduleDefaults(profile.data.schedule_defaults);
      const isConfigured = Object.keys(config).length > 0;
      return {
        enabled: config.enabled === true,
        timeZone: typeof config.time_zone === "string" ? config.time_zone
          : !isConfigured && typeof settings.time_zone === "string" ? settings.time_zone : null,
        // Never set up: the accounts it starts with, as the server seeds them.
        accountIds: ids(config.account_ids ?? settings.account_ids),
        // Only cadence is shared. Remembered hours seed an unconfigured project once.
        cadences: defaults?.cadences ?? {},
        preferredHours: Array.isArray(config.preferred_hours) ? config.preferred_hours
          : !isConfigured ? (Array.isArray(settings.preferred_hours) ? settings.preferred_hours : defaults?.preferredHours ?? null) : null,
        startAt: typeof config.start_at === "string" ? new Date(config.start_at) : null,
        upcoming: upcoming.count ?? 0,
        nextPostAt: upcoming.data[0] ? new Date(upcoming.data[0].scheduled_at) : null,
      };
    },
    // Background publishing and bounded planner runs can finish after this read.
    refetchInterval: query => query.state.data?.enabled ? 30_000 : false,
  });
  useEffect(() => {
    if (!enabled || !userId || !folderId) return;
    return queryClient.getQueryCache().subscribe(event => {
      if (event.type !== "updated" || event.query.queryKey[0] !== "publications" ||
        (event.action.type !== "invalidate" && event.action.type !== "success")) return;
      void queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false });
    });
  }, [enabled, userId, folderId, queryClient]);

  const save = useMutation({
    // Keep optimistic controls in order per project. The API atomically merges
    // these partial updates with the latest persisted settings.
    scope: { id: `auto-publish-${folderId}` },
    mutationFn: async (change: AutoPublishChange) => {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/v2/projects/${encodeURIComponent(folderId)}/auto-publish`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token ?? ""}` },
        body: JSON.stringify({
          enabled: change.enabled,
          account_ids: change.accountIds,
          cadences: change.cadences,
          preferred_hours: change.preferredHours,
          start_at: change.startAt === undefined ? undefined : change.startAt?.toISOString() ?? null,
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) throw new Error(body?.message ?? `Couldn't save ${AUTO_POST}.`);
    },
    // The switch and chips answer at once; the server's bookings follow.
    onMutate: async (change) => {
      const projectsKey = autoPublishProjectsQueryKey(userId);
      await Promise.all([
        queryClient.cancelQueries({ queryKey }),
        queryClient.cancelQueries({ queryKey: projectsKey }),
      ]);
      const previous = queryClient.getQueriesData<AutoPublishState | null>({ queryKey });
      const previousState = queryClient.getQueryData<AutoPublishState | null>([...queryKey, refreshKey]) ?? null;
      const previousProject = queryClient.getQueryData<FolderModel[]>(projectsKey)?.find(project => project.id === folderId);
      queryClient.setQueriesData<AutoPublishState | null>({ queryKey }, (state) => state && ({
        ...applyAutoPublishChange(state, change),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }));
      queryClient.setQueryData<FolderModel[]>(projectsKey, projects => projects && applyAutoPublishProjectChange(projects, folderId, change));
      const optimisticProject = queryClient.getQueryData<FolderModel[]>(projectsKey)?.find(project => project.id === folderId);
      return { folderId, previousState, previous, projectsKey, previousProject, optimisticProject };
    },
    onError: (error, _change, context) => {
      for (const [key, data] of context?.previous ?? []) queryClient.setQueryData(key, data);
      if (context?.previousProject) {
        // Restore only this project's optimistic row. Other projects can be
        // toggled concurrently, and a newer update to this row must also win.
        queryClient.setQueryData<FolderModel[]>(context.projectsKey, projects => projects && sortAutoPublishProjects(
          projects.map(project => {
            const optimistic = context.optimisticProject?.autoPublish;
            const current = project.autoPublish;
            // React Query can replace row references when active-first sorting
            // moves array entries, so compare the fields this mutation changed.
            const stillOptimistic = project.id === folderId
              && current?.enabled === optimistic?.enabled
              && current?.accountIds.length === optimistic?.accountIds.length
              && current?.accountIds.every((id, index) => id === optimistic?.accountIds[index]);
            return stillOptimistic ? { ...project, autoPublish: context.previousProject!.autoPublish } : project;
          }),
        ));
      }
      toast.error(error.message);
    },
    onSettled: () => {
      // Keep the mutation pending until the queue and shared settings have
      // refreshed, so the spinner covers the bookings that follow the save.
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ["auto-publish"] }),
        queryClient.invalidateQueries({ queryKey: autoPublishDefaultsQueryKey(userId) }),
        queryClient.invalidateQueries({ queryKey: autoPublishProjectsQueryKey(userId) }),
        queryClient.invalidateQueries({ queryKey: ["publications"] }),
        queryClient.invalidateQueries({ queryKey: upcomingAutoPostQueryKey(userId) }, { cancelRefetch: false }),
        queryClient.invalidateQueries({ queryKey: ["resources"] }),
        queryClient.invalidateQueries({ queryKey: scheduleDefaultsQueryKey(userId) }),
      ]);
    },
  });

  const isSaving = save.isPending && (!save.context || save.context.folderId === folderId);
  // Keep presentation on the confirmed snapshot while controls update optimistically.
  // Refetches can finish before the mutation settles; release the snapshot only then.
  const statusState = isSaving && save.context?.folderId === folderId
    ? save.context.previousState : query.data ?? null;

  return {
    state: query.data ?? null,
    statusState,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    isSaving,
    isStarting: isSaving && save.variables?.enabled === true,
    save: save.mutate,
  };
}

export type AutoPublishSubmit = { enabled: boolean; time_zone: string; account_ids: string[];
  cadences?: Record<string, AccountCadence>; preferred_hours?: number[]; start_at: string | null };

/**
 * The submit form's Auto publish, before there is a project: it starts where
 * the last submit left it (on or off, its accounts, the owner's rhythm) and
 * is sent with the task. On needs an account to post to.
 */
export function useAutoPublishDraft(enabled: boolean) {
  const supabase = useSupabaseClient();
  const userId = useSession()?.user.id;
  const accountsQuery = useSocialProviderAccounts().readAccounts;
  const targets = autoPublishAccounts(accountsQuery.data ?? []);
  const savedQuery = useQuery({
    queryKey: autoPublishDefaultsQueryKey(userId),
    enabled: enabled && Boolean(userId),
    queryFn: async () => {
      const { data, error } = await supabase.from("profiles").select("auto_publish, schedule_defaults").eq("id", userId!).single();
      if (error) throw error;
      const settings = record(data.auto_publish);
      return {
        defaultOn: settings.default_on === true,
        scheduleDefaults: parseScheduleDefaults(data.schedule_defaults),
        accountIds: ids(settings.account_ids),
        preferredHours: Array.isArray(settings.preferred_hours) ? (settings.preferred_hours as number[]) : null,
      };
    },
  });
  const saved = savedQuery.data;
  const scheduleDefaults = saved?.scheduleDefaults;
  const [changes, setChanges] = useState<AutoPublishChange>({});
  // The last submit's on carries over only if there was somewhere to post
  // when the form became ready: an account connected afterwards (Connect
  // account, a reconnect, another tab) never turns it on by itself. The
  // switch is the only way on then, through its plan check.
  const startsOn = useRef<boolean | null>(null);
  if (startsOn.current === null && saved !== undefined && accountsQuery.data !== undefined)
    startsOn.current = saved.defaultOn && targets.length > 0;
  const accountIds = (changes.accountIds ?? defaultAutoPublishAccountIds(targets, saved?.accountIds ?? [], scheduleDefaults?.accountIds ?? []))
    .filter((id) => targets.some((account) => account.id === id));
  const state: AutoPublishState = {
    enabled: (changes.enabled ?? startsOn.current ?? false) && accountIds.length > 0,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    accountIds,
    // The Schedule page's cadences, and its hours until Auto post has its own.
    cadences: { ...scheduleDefaults?.cadences, ...changes.cadences },
    preferredHours: changes.preferredHours ?? saved?.preferredHours ?? scheduleDefaults?.preferredHours ?? null,
    startAt: changes.startAt ?? null,
    upcoming: 0,
    nextPostAt: null,
  };
  const change = (next: AutoPublishChange) => setChanges((current) => ({
    ...current, ...next, ...(next.cadences && { cadences: { ...current.cadences, ...next.cadences } }),
  }));
  const payload: AutoPublishSubmit = {
    enabled: state.enabled,
    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    account_ids: state.accountIds,
    // Only cadences changed here: the rest are the Schedule page's already.
    ...(changes.cadences && { cadences: changes.cadences }),
    ...(state.preferredHours && { preferred_hours: state.preferredHours }),
    start_at: state.startAt?.toISOString() ?? null,
  };
  const error = savedQuery.error ?? accountsQuery.error;
  const isReady = saved !== undefined && accountsQuery.data !== undefined && !error;
  const retry = () => Promise.all([savedQuery.refetch(), accountsQuery.refetch()]);
  return { state, change, targets, rawAccounts: accountsQuery.data ?? [], payload, isReady, error, retry };
}

/**
 * The accounts a new project posts to: the ones Auto publish used last, else
 * the Schedule page's, else one per platform. Only accounts it can post to.
 */
export function defaultAutoPublishAccountIds(
  accounts: readonly SocialProviderAccount[],
  lastUsed: readonly string[],
  scheduleDefaults: readonly string[],
): string[] {
  const usable = (list: readonly string[]) => list.filter((id) => accounts.some((account) => account.id === id));
  if (usable(lastUsed).length) return usable(lastUsed);
  if (usable(scheduleDefaults).length) return usable(scheduleDefaults);
  const providers = new Set<string>();
  return accounts.filter((account) => !providers.has(account.provider) && providers.add(account.provider)).map((account) => account.id);
}
